import {
  Drawer,
  Stack,
  Text,
  Title,
  Badge,
  Divider,
  Grid,
  Code,
  Group,
} from '@mantine/core';
import { MapPin } from 'lucide-react';
import { Device, DeviceState } from '../types';

interface DeviceDetailDrawerProps {
  device: Device;
  opened: boolean;
  onClose: () => void;
}

function getDeviceStateBadge(state: DeviceState) {
  const colors: Record<DeviceState, string> = {
    [DeviceState.PENDING]: 'yellow',
    [DeviceState.CONFIGURED]: 'blue',
    [DeviceState.ACTIVE]: 'green',
    [DeviceState.INACTIVE]: 'gray',
    [DeviceState.DISABLED]: 'red',
  };

  return <Badge size="lg" color={colors[state]}>{state.toUpperCase()}</Badge>;
}

export function DeviceDetailDrawer({ device, opened, onClose }: DeviceDetailDrawerProps) {
  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      title="Device Details"
    >
      <Stack gap="lg">
        {/* Status Badge */}
        {getDeviceStateBadge(device.state)}

        <Divider />

        {/* Device Information */}
        <Stack gap="md">
          <Title order={4}>Device Information</Title>

          <Grid>
            <Grid.Col span={4}>
              <Text size="sm" fw={500}>Sensor ID</Text>
            </Grid.Col>
            <Grid.Col span={8}>
              <Code>{device.sensor_id}</Code>
            </Grid.Col>

            <Grid.Col span={4}>
              <Text size="sm" fw={500}>MAC Address</Text>
            </Grid.Col>
            <Grid.Col span={8}>
              <Code>{device.mac_address}</Code>
            </Grid.Col>

            <Grid.Col span={4}>
              <Text size="sm" fw={500}>Sensor Name</Text>
            </Grid.Col>
            <Grid.Col span={8}>
              <Text size="sm">{device.sensor_name || <Text component="span" c="dimmed">Not set</Text>}</Text>
            </Grid.Col>

            {device.serial_number && (
              <>
                <Grid.Col span={4}>
                  <Text size="sm" fw={500}>Serial Number</Text>
                </Grid.Col>
                <Grid.Col span={8}>
                  <Code>{device.serial_number}</Code>
                </Grid.Col>
              </>
            )}

            <Grid.Col span={4}>
              <Text size="sm" fw={500}>Last Heartbeat</Text>
            </Grid.Col>
            <Grid.Col span={8}>
              <Text size="sm">
                {device.last_heartbeat
                  ? new Date(device.last_heartbeat).toLocaleString()
                  : 'Never'}
              </Text>
            </Grid.Col>

            <Grid.Col span={4}>
              <Text size="sm" fw={500}>Created</Text>
            </Grid.Col>
            <Grid.Col span={8}>
              <Text size="sm">{new Date(device.created_at).toLocaleString()}</Text>
            </Grid.Col>
          </Grid>
        </Stack>

        <Divider />

        {/* Location */}
        <Stack gap="md">
          <Title order={4}>Location</Title>
          <Grid>
            <Grid.Col span={4}>
              <Text size="sm" fw={500}>GPS Coordinates</Text>
            </Grid.Col>
            <Grid.Col span={8}>
              {device.location ? (
                <Group gap="xs">
                  <Badge color="green" size="sm" leftSection={<MapPin size={10} />}>
                    Set
                  </Badge>
                  <Text size="sm" ff="monospace">
                    {device.location.lat.toFixed(6)}, {device.location.lon.toFixed(6)}
                  </Text>
                </Group>
              ) : (
                <Text size="sm" c="dimmed">Not configured</Text>
              )}
            </Grid.Col>
          </Grid>
        </Stack>

        <Divider />

        {/* Hardware Information */}
        <Stack gap="md">
          <Title order={4}>Hardware Information</Title>

          <Grid>
            {device.hardware_info?.hardware_model && (
              <>
                <Grid.Col span={4}>
                  <Text size="sm" fw={500}>Model</Text>
                </Grid.Col>
                <Grid.Col span={8}>
                  <Text size="sm">{device.hardware_info.hardware_model}</Text>
                </Grid.Col>
              </>
            )}

            {device.hardware_info?.os_name && (
              <>
                <Grid.Col span={4}>
                  <Text size="sm" fw={500}>OS</Text>
                </Grid.Col>
                <Grid.Col span={8}>
                  <Text size="sm">
                    {device.hardware_info.os_name} {device.hardware_info.os_version || ''}
                  </Text>
                </Grid.Col>
              </>
            )}

            {device.hardware_info?.architecture && (
              <>
                <Grid.Col span={4}>
                  <Text size="sm" fw={500}>Architecture</Text>
                </Grid.Col>
                <Grid.Col span={8}>
                  <Text size="sm">{device.hardware_info.architecture}</Text>
                </Grid.Col>
              </>
            )}

            {device.hardware_info?.cpu_model && (
              <>
                <Grid.Col span={4}>
                  <Text size="sm" fw={500}>CPU</Text>
                </Grid.Col>
                <Grid.Col span={8}>
                  <Text size="sm">{device.hardware_info.cpu_model}</Text>
                </Grid.Col>
              </>
            )}

            {device.hardware_info?.total_memory && (
              <>
                <Grid.Col span={4}>
                  <Text size="sm" fw={500}>Memory</Text>
                </Grid.Col>
                <Grid.Col span={8}>
                  <Text size="sm">{device.hardware_info.total_memory}</Text>
                </Grid.Col>
              </>
            )}
          </Grid>
        </Stack>
      </Stack>
    </Drawer>
  );
}
